import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ChevronDown, LogOut, UserRound } from 'lucide-react';
import { useEffect, useState, type CSSProperties } from 'react';

/** Up to two initials: first and last word ("Kulani Jayathilake" → "KJ"), or the first two letters of a single word. */
export function initialsFor(name: string, fallback = ''): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  const first = words[0];
  const last = words[words.length - 1];
  const letters = words.length >= 2 && first && last ? `${first[0]}${last[0]}` : first ? first.slice(0, 2) : fallback.slice(0, 1);
  return letters.toUpperCase();
}

/** A stable hue per name, so each person keeps the same colour everywhere their initials appear. */
export function hueFor(name: string): number {
  let hash = 0;
  for (const character of name) hash = (hash * 31 + character.charCodeAt(0)) % 360;
  return hash;
}

export type AvatarProps = { name: string; imageUrl?: string | null | undefined; size?: number; className?: string };

/** A circular profile photo, or the person's initials on a colour of their own when there is no photo. */
export function Avatar({ name, imageUrl, size = 36, className }: AvatarProps) {
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [imageUrl]);
  const showImage = Boolean(imageUrl) && !failed;
  const style = { '--bv-avatar-size': `${size}px`, '--bv-avatar-hue': hueFor(name) } as CSSProperties;
  return <span className={`bv-avatar${className ? ` ${className}` : ''}`} style={style} aria-hidden="true">
    {showImage ? <img src={imageUrl ?? undefined} alt="" onError={() => setFailed(true)} /> : initialsFor(name)}
  </span>;
}

export type UserMenuProps = {
  name: string;
  email: string;
  avatarUrl?: string | null | undefined;
  onProfile: () => void;
  onLogout: () => void;
};

/** The signed-in person's avatar in the top bar, opening their account options. */
export function UserMenu({ name, email, avatarUrl, onProfile, onLogout }: UserMenuProps) {
  return <DropdownMenu.Root>
    <DropdownMenu.Trigger asChild>
      <button type="button" className="bv-user-menu__trigger" aria-label={`Account menu for ${name}`}>
        <Avatar name={name} imageUrl={avatarUrl} />
        <ChevronDown aria-hidden="true" size={16} className="bv-user-menu__chevron" />
      </button>
    </DropdownMenu.Trigger>
    <DropdownMenu.Portal>
      <DropdownMenu.Content className="bv-dropdown bv-user-menu" align="end" sideOffset={8}>
        <div className="bv-user-menu__header">
          <Avatar name={name} imageUrl={avatarUrl} size={40} />
          <div className="bv-user-menu__identity"><strong>{name}</strong><span>{email}</span></div>
        </div>
        <DropdownMenu.Separator className="bv-dropdown__separator" />
        <DropdownMenu.Item className="bv-dropdown__item" onSelect={onProfile}>
          <UserRound aria-hidden="true" size={16} /><span>My profile</span>
        </DropdownMenu.Item>
        <DropdownMenu.Item className="bv-dropdown__item bv-dropdown__item--danger" onSelect={onLogout}>
          <LogOut aria-hidden="true" size={16} /><span>Log out</span>
        </DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu.Portal>
  </DropdownMenu.Root>;
}
